import { useCallback } from 'react';

import { createActionsHook, createStateHook } from 'react-sweet-state';

import { EMPTY_SELECTED_AGENT_IDS } from './emptySelectedAgentIds';
import {
	selectedAgentDraftControllerStore,
	type SelectedAgentDraftControllerStore,
	type SelectedAgentDraftMetadata,
} from './selectedAgentDraftControllerStore';

type SelectedAgentDraftSlice = {
	selectedAgentIds: readonly string[];
	activeSelectedAgentId: string | undefined;
	activeSelectedAgentMetadata: SelectedAgentDraftMetadata | undefined;
};

const useConversationSelectedAgentDraftState = createStateHook(selectedAgentDraftControllerStore, {
	selector: (
		state: SelectedAgentDraftControllerStore,
		conversationId: string,
	): SelectedAgentDraftSlice => {
		const selectedAgentIds = state.selectedAgentIds[conversationId] ?? EMPTY_SELECTED_AGENT_IDS;
		const activeSelectedAgentId = selectedAgentIds.at(-1);
		return {
			selectedAgentIds,
			activeSelectedAgentId,
			activeSelectedAgentMetadata: activeSelectedAgentId
				? state.selectedAgentMetadata[conversationId]?.[activeSelectedAgentId]
				: undefined,
		};
	},
});

const useSelectedAgentDraftActions = createActionsHook(selectedAgentDraftControllerStore);

export const useSelectedAgentDraftController = (
	conversationId: string,
): SelectedAgentDraftSlice & {
	setSelectedAgentIds: (agentIds: string[]) => void;
	addSelectedAgentId: (agentId: string, metadata?: SelectedAgentDraftMetadata) => void;
	removeSelectedAgentId: (agentId: string) => void;
} => {
	const { selectedAgentIds, activeSelectedAgentId, activeSelectedAgentMetadata } =
		useConversationSelectedAgentDraftState(conversationId);
	const {
		setSelectedAgentIds: storeSetSelectedAgentIds,
		addSelectedAgentId: storeAddSelectedAgentId,
		removeSelectedAgentId: storeRemoveSelectedAgentId,
	} = useSelectedAgentDraftActions();

	const setSelectedAgentIds = useCallback(
		(agentIds: string[]): void => {
			storeSetSelectedAgentIds(conversationId, agentIds);
		},
		[conversationId, storeSetSelectedAgentIds],
	);

	const addSelectedAgentId = useCallback(
		(agentId: string, metadata?: SelectedAgentDraftMetadata): void => {
			storeAddSelectedAgentId(conversationId, agentId, metadata);
		},
		[conversationId, storeAddSelectedAgentId],
	);

	const removeSelectedAgentId = useCallback(
		(agentId: string): void => {
			storeRemoveSelectedAgentId(conversationId, agentId);
		},
		[conversationId, storeRemoveSelectedAgentId],
	);

	return {
		selectedAgentIds,
		activeSelectedAgentId,
		activeSelectedAgentMetadata,
		setSelectedAgentIds,
		addSelectedAgentId,
		removeSelectedAgentId,
	};
};
